import React, { useEffect, useState } from 'react'
import { Select, Checkbox, Row, Col, message, Divider } from 'antd'
import { getT3CCallNumbers } from '@services/robot'
import './PhoneNumberSelector.less'

export interface PhoneNumberItem {
  value: string
  label: string
}

interface SkillGroupData {
  skillGroupId: number
  skillGroupName: string
  numbers: Array<{ number: string }>
}

interface PhoneNumberSelectorProps {
  value?: string[]
  onChange?: (value?: string[]) => void
  placeholder?: string
}

const PhoneNumberSelector: React.FC<PhoneNumberSelectorProps> = ({
  value,
  onChange,
  placeholder = '请选择主叫号码'
}) => {
  const selectedValues = value || []
  const [skillGroups, setSkillGroups] = useState<SkillGroupData[]>([])
  const [allNumbers, setAllNumbers] = useState<string[]>([])

  // 获取电话号码列表
  useEffect(() => {
    const fetchPhoneList = async () => {
      const [res, err] = await getT3CCallNumbers({ page: 1, pageSize: 999, isAIOutbound: true })
      if (err) {
        message.error(`获取号码列表失败: ${err.message}`)
        return
      }

      // 保存技能组数据
      setSkillGroups(res.skillGroupNumbers || [])

      // 从技能组中提取所有号码并去重（用于全选）
      const allNumbersFromGroups = res.skillGroupNumbers?.flatMap(group =>
        group.numbers.map(item => item.number)
      ) || []
      const uniqueNumbers = [...new Set(allNumbersFromGroups)]
      setAllNumbers(uniqueNumbers)
    }

    fetchPhoneList()
  }, [])

  // 全选所有号码
  const handleSelectAll = (e: any) => {
    const checked = e.target.checked
    const newValues = checked ? allNumbers : undefined
    onChange?.(newValues)
  }

  // 全选某个技能组
  const handleSkillGroupSelectAll = (skillGroupId: number, checked: boolean) => {
    const skillGroup = skillGroups.find(sg => sg.skillGroupId === skillGroupId)
    if (!skillGroup) return

    const groupNumbers = skillGroup.numbers.map(n => n.number)
    let newValues: string[] | undefined

    if (checked) {
      // 添加该技能组的所有号码（去重）
      const combined = [...new Set([...selectedValues, ...groupNumbers])]
      newValues = combined
    } else {
      // 移除该技能组的所有号码
      const filtered = selectedValues.filter(v => !groupNumbers.includes(v))
      newValues = filtered.length > 0 ? filtered : undefined
    }
    onChange?.(newValues)
  }

  // 单个号码选择
  const handleCheckboxChange = (phoneValue: string, checked: boolean) => {
    let newValues: string[] | undefined
    if (checked) {
      newValues = [...selectedValues, phoneValue]
    } else {
      const filtered = selectedValues.filter(v => v !== phoneValue)
      newValues = filtered.length > 0 ? filtered : undefined
    }
    onChange?.(newValues)
  }

  const handleRowClick = (phoneValue: string) => {
    const isChecked = selectedValues.includes(phoneValue)
    handleCheckboxChange(phoneValue, !isChecked)
  }

  // 判断技能组是否全选
  const isSkillGroupAllSelected = (skillGroupId: number) => {
    const skillGroup = skillGroups.find(sg => sg.skillGroupId === skillGroupId)
    if (!skillGroup || skillGroup.numbers.length === 0) return false

    const groupNumbers = skillGroup.numbers.map(n => n.number)
    return groupNumbers.every(number => selectedValues.includes(number))
  }

  // 判断技能组是否半选
  const isSkillGroupIndeterminate = (skillGroupId: number) => {
    const skillGroup = skillGroups.find(sg => sg.skillGroupId === skillGroupId)
    if (!skillGroup || skillGroup.numbers.length === 0) return false

    const groupNumbers = skillGroup.numbers.map(n => n.number)
    const selectedCount = groupNumbers.filter(number => selectedValues.includes(number)).length
    return selectedCount > 0 && selectedCount < groupNumbers.length
  }

  const isAllSelected = allNumbers.length > 0 && selectedValues.length === allNumbers.length
  const isIndeterminate = selectedValues.length > 0 && selectedValues.length < allNumbers.length

  const dropdownRender = () => (
    <div className="phone-number-dropdown">
      <div className="phone-number-header">
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={handleSelectAll}
        >
          全选
        </Checkbox>
      </div>
      <div className="phone-number-list">
        <Row className="phone-number-list-header">
          <Col span={1}></Col>
          <Col span={23}>电话号码</Col>
        </Row>
        {skillGroups.map(skillGroup => (
          <div key={skillGroup.skillGroupId} className="skill-group-section">
            <div className="skill-group-header">
              <Checkbox
                checked={isSkillGroupAllSelected(skillGroup.skillGroupId)}
                indeterminate={isSkillGroupIndeterminate(skillGroup.skillGroupId)}
                onChange={(e) => handleSkillGroupSelectAll(skillGroup.skillGroupId, e.target.checked)}
              >
                <span className="skill-group-name">{skillGroup.skillGroupName}</span>
                <span className="skill-group-count">({skillGroup.numbers.length}个号码)</span>
              </Checkbox>
            </div>
            {skillGroup.numbers.map(item => {
              const isChecked = selectedValues.includes(item.number)
              return (
                <Row
                  key={item.number}
                  className="phone-number-item"
                  onClick={() => handleRowClick(item.number)}
                >
                  <Col span={1}>
                    <Checkbox
                      checked={isChecked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleCheckboxChange(item.number, e.target.checked)}
                    />
                  </Col>
                  <Col span={23} className="phone-number-text">{item.number}</Col>
                </Row>
              )
            })}
            <Divider style={{ margin: '8px 0' }} />
          </div>
        ))}
      </div>
    </div>
  )

  const displayValue = selectedValues.length > 0
    ? `已选择 ${selectedValues.length} 个号码`
    : undefined

  return (
    <Select
      mode="multiple"
      value={selectedValues.length > 0 ? selectedValues : undefined}
      placeholder={placeholder}
      dropdownRender={dropdownRender}
      maxTagCount={0}
      maxTagPlaceholder={displayValue}
      style={{ width: '100%' }}
      showArrow
      allowClear
      onClear={() => {
        onChange?.(undefined)
      }}
    />
  )
}

export default PhoneNumberSelector
